import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { EQUIPMENT_LABELS } from './ExerciseMetaForm';
import { similarExercises } from '../lib/planEditing';
import { Info, Search } from 'lucide-react';

const chip = (active) => `px-3 py-1.5 rounded-full text-sm border transition-all ${
  active ? 'bg-[#C9A84C] border-[#C9A84C] text-[#0A0A0A] font-semibold' : 'border-[#2A2A2A] text-[#A1A1A1] hover:border-[#C9A84C]/50'
}`;

// Shared list for adding an exercise and for swapping one (`replacing` = the plan item).
// A swap opens on similar movements, filtered by equipment on request.
export const ExercisePicker = ({ open, onOpenChange, exercises, replacing, onPick, onPreview }) => {
  const [query, setQuery] = useState('');
  const [equipment, setEquipment] = useState(null);
  const [allMovements, setAllMovements] = useState(false);

  const similar = useMemo(() => similarExercises(replacing, exercises), [replacing, exercises]);
  const showSimilar = !!replacing && similar.length > 0 && !allMovements;
  const pool = showSimilar ? similar : exercises.filter(ex => ex.id !== replacing?.exercise_id);
  const equipmentOptions = [...new Set(pool.flatMap(ex => ex.equipment || []))]
    .filter(eq => EQUIPMENT_LABELS[eq] && eq !== 'none');
  const shown = pool.filter(ex => ex.name.toLowerCase().includes(query.toLowerCase())
    && (!equipment || (ex.equipment || []).includes(equipment)));

  const close = (value) => {
    if (!value) { setQuery(''); setEquipment(null); setAllMovements(false); }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] rounded-3xl max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-text text-2xl text-white text-left">
            {replacing ? `Zamień: ${replacing.exercise_name}` : 'Wybierz ćwiczenie'}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 min-w-0">
          <div className="relative mb-3">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A1A1A1]" />
            <Input
              placeholder="Szukaj ćwiczenia..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-[#0A0A0A] border-[#2A2A2A] rounded-full text-white pl-12"
            />
          </div>
          {replacing && similar.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3" data-testid="picker-scope">
              <button onClick={() => setAllMovements(false)} className={chip(!allMovements)}>Podobny ruch</button>
              <button onClick={() => setAllMovements(true)} className={chip(allMovements)}>Wszystkie</button>
            </div>
          )}
          {equipmentOptions.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="picker-equipment">
              <button onClick={() => setEquipment(null)} className={chip(!equipment)}>Każdy sprzęt</button>
              {equipmentOptions.map(eq => (
                <button key={eq} onClick={() => setEquipment(eq === equipment ? null : eq)} className={chip(eq === equipment)}>
                  {EQUIPMENT_LABELS[eq]}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {shown.length === 0 && <p className="text-sm text-[#A1A1A1] py-6 text-center">Brak ćwiczeń dla tego wyboru.</p>}
            {shown.map(ex => (
              <div
                key={ex.id}
                className="w-full flex items-center gap-2 p-4 rounded-2xl bg-[#0A0A0A] hover:bg-[#2A2A2A] border border-[#2A2A2A] hover:border-[#C9A84C]/50 transition-all"
              >
                <button onClick={() => { onPick(ex); close(false); }} className="flex-1 min-w-0 text-left">
                  <div className="text-white font-medium mb-1">{ex.name}</div>
                  <div className="text-sm text-[#A1A1A1] line-clamp-1">
                    {(ex.equipment || []).map(eq => EQUIPMENT_LABELS[eq]).filter(Boolean).join(', ') || ex.description}
                  </div>
                </button>
                <button
                  onClick={() => onPreview(ex)}
                  className="text-[#A1A1A1] hover:text-[#C9A84C] shrink-0 p-2"
                  aria-label="Zobacz instrukcję"
                  title="Zobacz instrukcję"
                >
                  <Info className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
